// @ts-strict-ignore
import type { ValidationRejection } from "@altostra/type-validations";

import { FetchInvalidPayloadShapeError, FetchRequestError } from "@lib/errors";
import { PUSHER_CONFIG } from "@lib/permissionConstants";

import { PUSHER_ERROR_STATUS, PUSHER_SUBSCRIPTION_SUCCEEDED } from "../constants";
import type { AllRouteEndpoints } from "../routeTypes";
import type {
  GetPostWebsocketFetcherFunctionWithShadowError,
  InternalPostWebsocketFetcherFunctionWithShadowError,
} from "../types";
import { reactQueryPostWebsocketAdapterV2 } from "./reactQueryPostWebsocketAdapterV2";
import type { BaseAsyncProps, EndpointQueryKey, PusherDeveloperBindings } from "./types";
import {
  decompressPusherResponseV2,
  isPusherError,
  logToDataDog,
  getEventName,
  registerChunkSubscription,
  mergeChunkedPayloadIfNeeded,
  resolveChunkedMergeConfig,
} from "./utils";
/**
 * Adapter from react-query fetcher function to a more natural fetcher function.
 *
 * This takes the fetcher function and passes in the query key, minus the root cache key
 * to the function. It also passes the abort signal as an optional last parameter
 */
export const reactQueryPostWebsocketAdapter = <
  CacheKey extends AllRouteEndpoints,
  Arguments extends BaseAsyncProps,
  Result,
  Error extends FetchRequestError = FetchRequestError,
>(
  cacheKey: CacheKey,
  fetcherFunction: InternalPostWebsocketFetcherFunctionWithShadowError<Error, Arguments>,
  validatePusherResult: (
    result: unknown,
    rejectionReasons?: (rejection: ValidationRejection) => void
  ) => result is Result
): GetPostWebsocketFetcherFunctionWithShadowError<Error, CacheKey, Arguments, Result> => {
  const v2Adapter = reactQueryPostWebsocketAdapterV2(cacheKey, fetcherFunction, validatePusherResult);

  // convert jobToken into an optional property, so we can use it in faking if necessary
  return async (
    {
      queryKey: [{ cacheKey: endpoint }, args],
      signal,
      meta,
    }: EndpointQueryKey<CacheKey, [Omit<Arguments, "jobToken"> & { jobToken?: string }]>,
    customizedFetcherFunction: Function = undefined,
    useReactQueryPostWebsocketAdapterV2 = false
  ) => {
    if (useReactQueryPostWebsocketAdapterV2) {
      return (v2Adapter as any)(
        {
          queryKey: [{ cacheKey: endpoint }, args],
          signal,
          meta,
        },
        customizedFetcherFunction
      );
    }
    if (customizedFetcherFunction) {
      const jobToken = args.jobToken;
      const augmentedArgs: Arguments = { ...args, jobToken } as Arguments;
      return customizedFetcherFunction(signal, augmentedArgs);
    }

    const extractedRequestId = (args as any)?.meta?.requestId || undefined;

    const callbacks: {
      onSuccess: (result: unknown) => void;
      onReconnect: () => void;
      onCancellation: (data: { request_id: string }, storedRequestId?: string) => void;
      cachedSuccess: unknown;
      reconnected: boolean;
      cancelled: boolean;
    } = {
      onSuccess: (result) => {
        callbacks.cachedSuccess = result;
      },
      onReconnect: () => {
        callbacks.reconnected = true;
      },
      onCancellation: (data, storedRequestId) => {
        const { request_id: cancelRequestId } = data;

        if (storedRequestId && cancelRequestId && storedRequestId === cancelRequestId) {
          callbacks.cancelled = true;
        }
      },
      cachedSuccess: undefined,
      reconnected: false,
      cancelled: false,
    };

    const pusher = meta.pusher as PusherDeveloperBindings;
    const logException = meta.logException as ((error: Error) => void) | undefined;

    // Bind to the subscription succeeded event
    pusher.bind(PUSHER_SUBSCRIPTION_SUCCEEDED, callbacks.onReconnect);

    // Bind to the cancellation event to listen for our specific request cancellation
    pusher.bind(PUSHER_CONFIG.segmentStatsCancellationUpdate, (data) =>
      callbacks.onCancellation(data as any, extractedRequestId)
    );

    const jobToken = args.jobToken;
    const eventName = getEventName(jobToken);

    const chunkCleanupFns: Array<() => void> = [];
    const chunkedMergeConfig = resolveChunkedMergeConfig(args.chunkedMerge);
    const chunkState = registerChunkSubscription({
      enabled: !!chunkedMergeConfig,
      pusher,
      jobToken,
      onCleanup: (fn) => chunkCleanupFns.push(fn),
    });

    pusher.bind(eventName, callbacks.onSuccess);

    // for datadog logging
    // eslint-disable-next-line local-eslint-rules/no-new-date
    const startTime = new Date().getTime();
    const { estimated } = args;
    const augmentedArgs: Arguments = { ...args, jobToken } as Arguments;

    try {
      await fetcherFunction(signal, augmentedArgs);

      const result = await Promise.race([
        // listen for the result of the async process and return it
        new Promise((resolve) => {
          if (callbacks.cachedSuccess) {
            // sometimes the result comes back before the HTTP request
            // in this case, we have it immediately and can return
            resolve(callbacks.cachedSuccess);
            return;
          }
          pusher.bind(eventName, (result) => {
            resolve(result);
          });
        }),
        // listen for pusher disconnect/reconnect, which results in the need to start over
        new Promise((_, reject) => {
          pusher.unbind(PUSHER_SUBSCRIPTION_SUCCEEDED, callbacks.onReconnect);
          callbacks.onReconnect = () => {
            // when we abort because a new request has been initiated, stop listening to the channel
            reject(new DOMException("", "AbortError"));
          };
          if (callbacks.reconnected) {
            callbacks.reconnected = false;
            callbacks.onReconnect();
          } else {
            pusher.bind(PUSHER_SUBSCRIPTION_SUCCEEDED, callbacks.onReconnect);
          }
        }),
        // listen for request cancellation from AsyncRequestManager
        new Promise((_, reject) => {
          // Check if already cancelled
          if (callbacks.cancelled) {
            reject(new FetchRequestError(499, "Request cancelled by client"));
            return;
          }

          // Set up cancellation check interval
          const checkCancellation = setInterval(() => {
            if (callbacks.cancelled) {
              clearInterval(checkCancellation);
              reject(new FetchRequestError(499, "Request cancelled by client"));
            }
          }, 1000);

          // Clean up interval if promise is resolved or rejected otherwise
          signal?.addEventListener("abort", () => clearInterval(checkCancellation));
        }),
        // if there is a timeout specified, fail after it is reached
        args.timeout
          ? new Promise((_, reject) => {
              setTimeout(() => {
                reject(new FetchRequestError(504, "Request timeout"));
              }, args.timeout);
            })
          : // never resolves
            new Promise(() => {}),
      ]);

      let pusherResponse = decompressPusherResponseV2<Result>(result);
      pusherResponse = await mergeChunkedPayloadIfNeeded<Result>(chunkedMergeConfig, pusherResponse, chunkState);
      logToDataDog({
        actionName: isPusherError(pusherResponse) ? "pusher-request-fetch-error" : "pusher-request-fetch-resolve",
        url: endpoint,
        errorText: isPusherError(pusherResponse) ? pusherResponse.pusher_error : "ok",

        // eslint-disable-next-line local-eslint-rules/no-new-date
        loadDuration: new Date().getTime() - startTime,
        estimated: estimated ? true : false,
      });
      if (isPusherError(pusherResponse)) {
        throw new FetchRequestError(PUSHER_ERROR_STATUS, pusherResponse.pusher_error);
      } else {
        FetchInvalidPayloadShapeError.validateShape({
          cacheKey,
          result: pusherResponse,
          validator: validatePusherResult,
          statusCode: PUSHER_ERROR_STATUS,
          rejectionMessage: "Invalid pusher response",
          logException,
        });

        return pusherResponse as Result;
      }
    } finally {
      chunkCleanupFns.forEach((fn) => fn());
      // clean up event listeners in all cases
      pusher.unbind(eventName);
      pusher.unbind(PUSHER_SUBSCRIPTION_SUCCEEDED, callbacks.onReconnect);
      pusher.unbind(PUSHER_CONFIG.segmentStatsCancellationUpdate, (data) =>
        callbacks.onCancellation(data as any, extractedRequestId)
      );
    }
  };
};
